import React, { useCallback, useMemo } from 'react';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import filter from 'lodash/filter';
import some from 'lodash/some';

import {
  MultiSelectChangeCallbackType,
  MultiSelectInputChangeCallbackType
} from '../../../../MultiSelect/types';
import {
  CurrencyMultiSelectOptionalDataProps,
  CurrencyMultiSelectValueType
} from './CurrencySelectFieldControl.types';

import {
  MultiSelect,
  MultiSelectProps,
  MultiSelectValueType
} from '../../../../MultiSelect';

interface CurrencySelectFieldControlProps {
  isLoading?: boolean;
  isSearchable?: boolean;
  multi: boolean;
  onChange: (updatedValue) => void;
  onInputChange?: MultiSelectInputChangeCallbackType;
  onMenuScrollToBottom?: () => void;
  optionsLoading?: boolean;
  value?: CurrencyMultiSelectValueType;
}

function CurrencySelectFieldControl({
  classNamePrefix = null,
  closeMenuOnSelect = true,
  data = [],
  defaultValue = null,
  disabled = false,
  emptyValue = null,
  error,
  errorClassName,
  i18nLabel = null,
  i18nPlaceholder,
  inputWrapperClassName = null,
  isClearable = false,
  isLoading = false,
  isSearchable = false,
  labelClassName,
  menuIsOpen,
  menuPlacement = 'auto',
  menuPosition,
  multi = false,
  name,
  onBlur,
  onChange,
  onInputChange,
  onMenuScrollToBottom,
  optionsLoading = false,
  placeholder = null,
  showError,
  value,
  withPortal
}: MultiSelectProps &
  CurrencySelectFieldControlProps &
  CurrencyMultiSelectOptionalDataProps) {
  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (updateValue) => {
      onChange(
        isArray(updateValue) ? map(updateValue, 'value') : updateValue?.value
      );
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectValueType>(
    () =>
      filter(data, (option) =>
        isArray(value)
          ? some(value, (v) => v === option.value)
          : value === option.value
      ),
    [data, value]
  );

  return (
    <MultiSelect
      classNamePrefix={classNamePrefix}
      closeMenuOnSelect={closeMenuOnSelect}
      data={data}
      defaultValue={defaultValue}
      disabled={disabled}
      emptyValue={emptyValue}
      error={error}
      errorClassName={errorClassName}
      i18nLabel={i18nLabel}
      i18nPlaceholder={i18nPlaceholder}
      inputWrapperClassName={inputWrapperClassName}
      isClearable={isClearable}
      isLoading={isLoading}
      isSearchable={isSearchable}
      labelClassName={labelClassName}
      menuIsOpen={menuIsOpen}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      multi={multi}
      name={name}
      onBlur={onBlur}
      onChange={handleChange}
      onInputChange={onInputChange}
      onMenuScrollToBottom={onMenuScrollToBottom}
      optionsLoading={optionsLoading}
      placeholder={placeholder}
      showError={showError}
      value={multiSelectValue}
      withPortal={withPortal}
    />
  );
}

export default CurrencySelectFieldControl;
